import { CompanyEntity } from "../../Objects/Company/CompanyEntity"
import { normalizeHexColor } from "../../Objects/Generics/Helps"

export type PublicMenuCardLayout = "cover" | "compact-left" | "compact-right"

export interface IPublicMenuTheme {
  cardLayout: PublicMenuCardLayout
  primaryColor: string
  secondaryColor: string
  accentColor: string
  backgroundColor: string
  surfaceColor: string
  textColor: string
  borderColor: string
  buttonColor: string
  buttonTextColor: string
  searchBackgroundColor: string
  searchBorderColor: string
  mobileNavBackgroundColor: string
  mobileNavActiveBackgroundColor: string
  mobileNavActiveTextColor: string
  mobileNavActiveIconColor: string
  mobileNavInactiveBackgroundColor: string
  mobileNavInactiveTextColor: string
  mobileNavInactiveIconColor: string
  headerBackgroundColor: string
  restaurantNameColor: string
  restaurantDescriptionColor: string
  statusCardBackgroundColor: string
  cartButtonColor: string
  cartBackgroundColor: string
  cartBorderColor: string
  cartTitleTextColor: string
  cartDescriptionTextColor: string
  cartTotalLabelTextColor: string
  cartTotalValueTextColor: string
  emptyStateTitleColor: string
  emptyStateDescriptionColor: string
  categoryBackgroundColor: string
  categoryBorderColor: string
  categoryDividerColor: string
  loginButtonBackgroundColor: string
  loginButtonTextColor: string
  shareButtonBackgroundColor: string
  shareButtonTextColor: string
  serviceSelectorBackgroundColor: string
  serviceSelectorTextColor: string
  serviceSelectorActiveBackgroundColor: string
  serviceSelectorActiveTextColor: string
  borderThickness: number
  highlightPromotions: boolean
  compactCategories: boolean
  useGradientHeader: boolean
}

export const LEGACY_PUBLIC_MENU_THEME: IPublicMenuTheme = {
  cardLayout: "cover",
  primaryColor: "#F97316",
  secondaryColor: "#F3F4F6",
  accentColor: "#DC2626",
  backgroundColor: "#FFFFFF",
  surfaceColor: "#FFFFFF",
  textColor: "#111827",
  borderColor: "#F3F4F6",
  buttonColor: "#10B981",
  buttonTextColor: "#FFFFFF",
  searchBackgroundColor: "#FFFFFF",
  searchBorderColor: "#E5E7EB",
  mobileNavBackgroundColor: "#FFFFFF",
  mobileNavActiveBackgroundColor: "#FFEDD5",
  mobileNavActiveTextColor: "#EA580C",
  mobileNavActiveIconColor: "#C2410C",
  mobileNavInactiveBackgroundColor: "#FFFFFF",
  mobileNavInactiveTextColor: "#9CA3AF",
  mobileNavInactiveIconColor: "#6B7280",
  headerBackgroundColor: "#FFFFFF",
  restaurantNameColor: "#030712",
  restaurantDescriptionColor: "#6B7280",
  statusCardBackgroundColor: "#ECFDF5",
  cartButtonColor: "#EA580C",
  cartBackgroundColor: "#FFFFFF",
  cartBorderColor: "#FED7AA",
  cartTitleTextColor: "#F97316",
  cartDescriptionTextColor: "#6B7280",
  cartTotalLabelTextColor: "#9CA3AF",
  cartTotalValueTextColor: "#111827",
  emptyStateTitleColor: "#111827",
  emptyStateDescriptionColor: "#6B7280",
  categoryBackgroundColor: "#FFFFFF",
  categoryBorderColor: "#F3F4F6",
  categoryDividerColor: "#F3F4F6",
  loginButtonBackgroundColor: "#FFFFFF",
  loginButtonTextColor: "#111827",
  shareButtonBackgroundColor: "#FFF7ED",
  shareButtonTextColor: "#15803D",
  serviceSelectorBackgroundColor: "#F3F4F6",
  serviceSelectorTextColor: "#111827",
  serviceSelectorActiveBackgroundColor: "#F97316",
  serviceSelectorActiveTextColor: "#111827",
  borderThickness: 1,
  highlightPromotions: true,
  compactCategories: false,
  useGradientHeader: false,
}

export const normalizeBorderThickness = (value?: number | null, fallback = 1) => {
  if (typeof value !== "number" || Number.isNaN(value)) {
    return fallback
  }

  return Math.max(1, Math.min(4, Math.round(value)))
}

export const getPublicMenuTheme = (company?: CompanyEntity | null): IPublicMenuTheme => {
  const cardLayout = company?.publicMenuCardLayout
  const validCardLayout: PublicMenuCardLayout =
    cardLayout === "compact-left" || cardLayout === "compact-right" ? cardLayout : LEGACY_PUBLIC_MENU_THEME.cardLayout
  const backgroundColor = normalizeHexColor(company?.publicMenuBackgroundColor, LEGACY_PUBLIC_MENU_THEME.backgroundColor)

  return {
    cardLayout: validCardLayout,
    primaryColor: normalizeHexColor(company?.publicMenuPrimaryColor, LEGACY_PUBLIC_MENU_THEME.primaryColor),
    secondaryColor: normalizeHexColor(company?.publicMenuSecondaryColor, LEGACY_PUBLIC_MENU_THEME.secondaryColor),
    accentColor: normalizeHexColor(company?.publicMenuAccentColor, LEGACY_PUBLIC_MENU_THEME.accentColor),
    backgroundColor,
    surfaceColor: normalizeHexColor(company?.publicMenuSurfaceColor, LEGACY_PUBLIC_MENU_THEME.surfaceColor),
    textColor: normalizeHexColor(company?.publicMenuTextColor, LEGACY_PUBLIC_MENU_THEME.textColor),
    borderColor: normalizeHexColor(company?.publicMenuBorderColor, LEGACY_PUBLIC_MENU_THEME.borderColor),
    buttonColor: normalizeHexColor(company?.publicMenuButtonColor, LEGACY_PUBLIC_MENU_THEME.buttonColor),
    buttonTextColor: normalizeHexColor(company?.publicMenuButtonTextColor, LEGACY_PUBLIC_MENU_THEME.buttonTextColor),
    searchBackgroundColor: normalizeHexColor(
      company?.publicMenuSearchBackgroundColor,
      backgroundColor,
    ),
    searchBorderColor: normalizeHexColor(company?.publicMenuSearchBorderColor, LEGACY_PUBLIC_MENU_THEME.searchBorderColor),
    mobileNavBackgroundColor: normalizeHexColor(
      company?.publicMenuMobileNavBackgroundColor,
      backgroundColor,
    ),
    mobileNavActiveBackgroundColor: normalizeHexColor(
      company?.publicMenuMobileNavActiveBackgroundColor,
      LEGACY_PUBLIC_MENU_THEME.mobileNavActiveBackgroundColor,
    ),
    mobileNavActiveTextColor: normalizeHexColor(
      company?.publicMenuMobileNavActiveTextColor,
      LEGACY_PUBLIC_MENU_THEME.mobileNavActiveTextColor,
    ),
    mobileNavActiveIconColor: normalizeHexColor(
      company?.publicMenuMobileNavActiveIconColor,
      LEGACY_PUBLIC_MENU_THEME.mobileNavActiveIconColor,
    ),
    mobileNavInactiveBackgroundColor: normalizeHexColor(
      company?.publicMenuMobileNavInactiveBackgroundColor,
      backgroundColor,
    ),
    mobileNavInactiveTextColor: normalizeHexColor(
      company?.publicMenuMobileNavInactiveTextColor,
      LEGACY_PUBLIC_MENU_THEME.mobileNavInactiveTextColor,
    ),
    mobileNavInactiveIconColor: normalizeHexColor(
      company?.publicMenuMobileNavInactiveIconColor,
      LEGACY_PUBLIC_MENU_THEME.mobileNavInactiveIconColor,
    ),
    headerBackgroundColor: normalizeHexColor(
      company?.publicMenuHeaderBackgroundColor,
      backgroundColor,
    ),
    restaurantNameColor: normalizeHexColor(company?.publicMenuRestaurantNameColor, LEGACY_PUBLIC_MENU_THEME.restaurantNameColor),
    restaurantDescriptionColor: normalizeHexColor(
      company?.publicMenuRestaurantDescriptionColor,
      LEGACY_PUBLIC_MENU_THEME.restaurantDescriptionColor,
    ),
    statusCardBackgroundColor: normalizeHexColor(
      company?.publicMenuStatusCardBackgroundColor,
      LEGACY_PUBLIC_MENU_THEME.statusCardBackgroundColor,
    ),
    cartButtonColor: normalizeHexColor(company?.publicMenuCartButtonColor, LEGACY_PUBLIC_MENU_THEME.cartButtonColor),
    cartBackgroundColor: normalizeHexColor(
      company?.publicMenuCartBackgroundColor,
      LEGACY_PUBLIC_MENU_THEME.cartBackgroundColor,
    ),
    cartBorderColor: normalizeHexColor(company?.publicMenuCartBorderColor, LEGACY_PUBLIC_MENU_THEME.cartBorderColor),
    cartTitleTextColor: normalizeHexColor(
      company?.publicMenuCartTitleTextColor,
      LEGACY_PUBLIC_MENU_THEME.cartTitleTextColor,
    ),
    cartDescriptionTextColor: normalizeHexColor(
      company?.publicMenuCartDescriptionTextColor,
      LEGACY_PUBLIC_MENU_THEME.cartDescriptionTextColor,
    ),
    cartTotalLabelTextColor: normalizeHexColor(
      company?.publicMenuCartTotalLabelTextColor,
      LEGACY_PUBLIC_MENU_THEME.cartTotalLabelTextColor,
    ),
    cartTotalValueTextColor: normalizeHexColor(
      company?.publicMenuCartTotalValueTextColor,
      LEGACY_PUBLIC_MENU_THEME.cartTotalValueTextColor,
    ),
    emptyStateTitleColor: normalizeHexColor(
      company?.publicMenuEmptyStateTitleColor,
      LEGACY_PUBLIC_MENU_THEME.emptyStateTitleColor,
    ),
    emptyStateDescriptionColor: normalizeHexColor(
      company?.publicMenuEmptyStateDescriptionColor,
      LEGACY_PUBLIC_MENU_THEME.emptyStateDescriptionColor,
    ),
    categoryBackgroundColor: normalizeHexColor(
      company?.publicMenuCategoryBackgroundColor,
      LEGACY_PUBLIC_MENU_THEME.categoryBackgroundColor,
    ),
    categoryBorderColor: normalizeHexColor(
      company?.publicMenuCategoryBorderColor,
      LEGACY_PUBLIC_MENU_THEME.categoryBorderColor,
    ),
    categoryDividerColor: normalizeHexColor(
      company?.publicMenuCategoryDividerColor,
      LEGACY_PUBLIC_MENU_THEME.categoryDividerColor,
    ),
    loginButtonBackgroundColor: normalizeHexColor(
      company?.publicMenuLoginButtonBackgroundColor,
      LEGACY_PUBLIC_MENU_THEME.loginButtonBackgroundColor,
    ),
    loginButtonTextColor: normalizeHexColor(company?.publicMenuLoginButtonTextColor, LEGACY_PUBLIC_MENU_THEME.loginButtonTextColor),
    shareButtonBackgroundColor: normalizeHexColor(
      company?.publicMenuShareButtonBackgroundColor,
      LEGACY_PUBLIC_MENU_THEME.shareButtonBackgroundColor,
    ),
    shareButtonTextColor: normalizeHexColor(company?.publicMenuShareButtonTextColor, LEGACY_PUBLIC_MENU_THEME.shareButtonTextColor),
    serviceSelectorBackgroundColor: normalizeHexColor(
      company?.publicMenuServiceSelectorBackgroundColor,
      LEGACY_PUBLIC_MENU_THEME.serviceSelectorBackgroundColor,
    ),
    serviceSelectorTextColor: normalizeHexColor(
      company?.publicMenuServiceSelectorTextColor,
      LEGACY_PUBLIC_MENU_THEME.serviceSelectorTextColor,
    ),
    serviceSelectorActiveBackgroundColor: normalizeHexColor(
      company?.publicMenuServiceSelectorActiveBackgroundColor,
      LEGACY_PUBLIC_MENU_THEME.serviceSelectorActiveBackgroundColor,
    ),
    serviceSelectorActiveTextColor: normalizeHexColor(
      company?.publicMenuServiceSelectorActiveTextColor,
      LEGACY_PUBLIC_MENU_THEME.serviceSelectorActiveTextColor,
    ),
    borderThickness: normalizeBorderThickness(
      company?.publicMenuBorderThickness,
      LEGACY_PUBLIC_MENU_THEME.borderThickness,
    ),
    highlightPromotions: company?.publicMenuHighlightPromotions ?? LEGACY_PUBLIC_MENU_THEME.highlightPromotions,
    compactCategories: company?.publicMenuCompactCategories ?? LEGACY_PUBLIC_MENU_THEME.compactCategories,
    useGradientHeader: company?.publicMenuUseGradientHeader ?? LEGACY_PUBLIC_MENU_THEME.useGradientHeader,
  }
}

export const isLegacyPublicMenuTheme = (theme: IPublicMenuTheme) =>
  theme.cardLayout === LEGACY_PUBLIC_MENU_THEME.cardLayout &&
  theme.primaryColor === LEGACY_PUBLIC_MENU_THEME.primaryColor &&
  theme.secondaryColor === LEGACY_PUBLIC_MENU_THEME.secondaryColor &&
  theme.accentColor === LEGACY_PUBLIC_MENU_THEME.accentColor &&
  theme.backgroundColor === LEGACY_PUBLIC_MENU_THEME.backgroundColor &&
  theme.surfaceColor === LEGACY_PUBLIC_MENU_THEME.surfaceColor &&
  theme.textColor === LEGACY_PUBLIC_MENU_THEME.textColor &&
  theme.borderColor === LEGACY_PUBLIC_MENU_THEME.borderColor &&
  theme.buttonColor === LEGACY_PUBLIC_MENU_THEME.buttonColor &&
  theme.buttonTextColor === LEGACY_PUBLIC_MENU_THEME.buttonTextColor &&
  theme.searchBackgroundColor === LEGACY_PUBLIC_MENU_THEME.searchBackgroundColor &&
  theme.searchBorderColor === LEGACY_PUBLIC_MENU_THEME.searchBorderColor &&
  theme.mobileNavBackgroundColor === LEGACY_PUBLIC_MENU_THEME.mobileNavBackgroundColor &&
  theme.mobileNavActiveBackgroundColor === LEGACY_PUBLIC_MENU_THEME.mobileNavActiveBackgroundColor &&
  theme.mobileNavActiveTextColor === LEGACY_PUBLIC_MENU_THEME.mobileNavActiveTextColor &&
  theme.mobileNavActiveIconColor === LEGACY_PUBLIC_MENU_THEME.mobileNavActiveIconColor &&
  theme.mobileNavInactiveBackgroundColor === LEGACY_PUBLIC_MENU_THEME.mobileNavInactiveBackgroundColor &&
  theme.mobileNavInactiveTextColor === LEGACY_PUBLIC_MENU_THEME.mobileNavInactiveTextColor &&
  theme.mobileNavInactiveIconColor === LEGACY_PUBLIC_MENU_THEME.mobileNavInactiveIconColor &&
  theme.headerBackgroundColor === LEGACY_PUBLIC_MENU_THEME.headerBackgroundColor &&
  theme.restaurantNameColor === LEGACY_PUBLIC_MENU_THEME.restaurantNameColor &&
  theme.restaurantDescriptionColor === LEGACY_PUBLIC_MENU_THEME.restaurantDescriptionColor &&
  theme.statusCardBackgroundColor === LEGACY_PUBLIC_MENU_THEME.statusCardBackgroundColor &&
  theme.cartButtonColor === LEGACY_PUBLIC_MENU_THEME.cartButtonColor &&
  theme.cartBackgroundColor === LEGACY_PUBLIC_MENU_THEME.cartBackgroundColor &&
  theme.cartBorderColor === LEGACY_PUBLIC_MENU_THEME.cartBorderColor &&
  theme.cartTitleTextColor === LEGACY_PUBLIC_MENU_THEME.cartTitleTextColor &&
  theme.cartDescriptionTextColor === LEGACY_PUBLIC_MENU_THEME.cartDescriptionTextColor &&
  theme.cartTotalLabelTextColor === LEGACY_PUBLIC_MENU_THEME.cartTotalLabelTextColor &&
  theme.cartTotalValueTextColor === LEGACY_PUBLIC_MENU_THEME.cartTotalValueTextColor &&
  theme.emptyStateTitleColor === LEGACY_PUBLIC_MENU_THEME.emptyStateTitleColor &&
  theme.emptyStateDescriptionColor === LEGACY_PUBLIC_MENU_THEME.emptyStateDescriptionColor &&
  theme.categoryBackgroundColor === LEGACY_PUBLIC_MENU_THEME.categoryBackgroundColor &&
  theme.categoryBorderColor === LEGACY_PUBLIC_MENU_THEME.categoryBorderColor &&
  theme.categoryDividerColor === LEGACY_PUBLIC_MENU_THEME.categoryDividerColor &&
  theme.loginButtonBackgroundColor === LEGACY_PUBLIC_MENU_THEME.loginButtonBackgroundColor &&
  theme.loginButtonTextColor === LEGACY_PUBLIC_MENU_THEME.loginButtonTextColor &&
  theme.shareButtonBackgroundColor === LEGACY_PUBLIC_MENU_THEME.shareButtonBackgroundColor &&
  theme.shareButtonTextColor === LEGACY_PUBLIC_MENU_THEME.shareButtonTextColor &&
  theme.serviceSelectorBackgroundColor === LEGACY_PUBLIC_MENU_THEME.serviceSelectorBackgroundColor &&
  theme.serviceSelectorTextColor === LEGACY_PUBLIC_MENU_THEME.serviceSelectorTextColor &&
  theme.serviceSelectorActiveBackgroundColor === LEGACY_PUBLIC_MENU_THEME.serviceSelectorActiveBackgroundColor &&
  theme.serviceSelectorActiveTextColor === LEGACY_PUBLIC_MENU_THEME.serviceSelectorActiveTextColor &&
  theme.borderThickness === LEGACY_PUBLIC_MENU_THEME.borderThickness &&
  theme.highlightPromotions === LEGACY_PUBLIC_MENU_THEME.highlightPromotions &&
  theme.compactCategories === LEGACY_PUBLIC_MENU_THEME.compactCategories &&
  theme.useGradientHeader === LEGACY_PUBLIC_MENU_THEME.useGradientHeader
